"use client";

import { useState, type FormEvent } from "react";
import Link from "next/link";
import { Check, ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { CONTACT_TOPICS, type ContactTopic } from "@/lib/contact";
import { cn } from "@/lib/utils";

// Mirrors the Input component's own classes (src/components/ui/input.tsx) so
// the native <select> and <textarea> — which have no component of their own
// here — sit at exactly the same standard control size as the Inputs above
// them. Labels are visually hidden; the placeholders carry the field names.
const control =
  "h-8 w-full min-w-0 rounded-lg border border-input bg-transparent px-2.5 py-1 text-base transition-colors outline-none placeholder:text-muted-foreground focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50 disabled:pointer-events-none disabled:cursor-not-allowed disabled:bg-input/50 disabled:opacity-50 md:text-sm dark:bg-input/30 dark:disabled:bg-input/80";

export function ContactForm() {
  const [name, setName] = useState("");
  const [website, setWebsite] = useState("");
  const [email, setEmail] = useState("");
  const [topic, setTopic] = useState<ContactTopic>(CONTACT_TOPICS[0]);
  const [message, setMessage] = useState("");
  const [accepted, setAccepted] = useState(false);

  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  const valid = name.trim() !== "" && email.trim() !== "" && message.trim() !== "" && accepted;

  // Any edit invalidates the "sent" confirmation — otherwise it would sit
  // there implying the message currently in the box was the one delivered.
  const touch = () => {
    setSent(false);
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!valid) {
      return;
    }
    setSending(true);
    setError("");
    try {
      const res = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name, website, email, topic, message }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(data.error || "Couldn't send your message. Try again.");
        return;
      }
      setSent(true);
      setName("");
      setWebsite("");
      setEmail("");
      setTopic(CONTACT_TOPICS[0]);
      setMessage("");
      setAccepted(false);
    } catch {
      setError("Couldn't reach the server. Try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <Card className="w-full [--card-spacing:--spacing(6)]">
      <CardHeader>
        <CardTitle className="text-lg font-semibold">Send a message</CardTitle>
      </CardHeader>

      <CardContent>
        <form onSubmit={handleSubmit} className="grid gap-3">
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <label htmlFor="contact-name" className="sr-only">
                Name
              </label>
              <Input
                id="contact-name"
                type="text"
                placeholder="Name"
                autoComplete="name"
                value={name}
                disabled={sending}
                onChange={(e) => {
                  setName(e.target.value);
                  touch();
                }}
              />
            </div>
            <div>
              <label htmlFor="contact-website" className="sr-only">
                Your website
              </label>
              <Input
                id="contact-website"
                type="text"
                placeholder="yoursite.com (optional)"
                autoComplete="url"
                value={website}
                disabled={sending}
                onChange={(e) => {
                  setWebsite(e.target.value);
                  touch();
                }}
              />
            </div>
          </div>

          <div>
            <label htmlFor="contact-email" className="sr-only">
              Email
            </label>
            <Input
              id="contact-email"
              type="email"
              placeholder="youremail@website.com"
              autoComplete="email"
              value={email}
              disabled={sending}
              onChange={(e) => {
                setEmail(e.target.value);
                touch();
              }}
            />
          </div>

          <div className="relative">
            <label htmlFor="contact-topic" className="sr-only">
              What&apos;s this about?
            </label>
            <select
              id="contact-topic"
              className={cn(control, "appearance-none pr-8 [&>option]:bg-background")}
              value={topic}
              disabled={sending}
              onChange={(e) => {
                setTopic(e.target.value as ContactTopic);
                touch();
              }}
            >
              {CONTACT_TOPICS.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
            <ChevronDown className="pointer-events-none absolute right-2.5 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground" />
          </div>

          <div>
            <label htmlFor="contact-message" className="sr-only">
              Message
            </label>
            <textarea
              id="contact-message"
              rows={5}
              className={cn(control, "h-auto resize-y py-1.5")}
              placeholder="Let us know what you need"
              value={message}
              disabled={sending}
              onChange={(e) => {
                setMessage(e.target.value);
                touch();
              }}
            />
          </div>

          <div className="mt-1 flex items-start gap-2.5">
            <Checkbox
              id="contact-terms"
              className="mt-0.5"
              checked={accepted}
              disabled={sending}
              onCheckedChange={(v) => {
                setAccepted(v === true);
                touch();
              }}
            />
            {/* A plain <label>, not the Label component — that one is a flex
                row with a gap, which would space out each word of a sentence
                like this and strand the trailing period on its own. */}
            <label
              htmlFor="contact-terms"
              className="text-[13px] leading-snug text-muted-foreground"
            >
              I have read and acknowledge the{" "}
              <Link href="/privacy" className="text-foreground underline underline-offset-2">
                Privacy Policy
              </Link>
              .
            </label>
          </div>

          <Button type="submit" className="mt-2 w-full" disabled={sending || !valid}>
            {sending ? "Sending…" : "Send message"}
          </Button>

          {error && <p className="m-0 text-[13px] text-destructive">{error}</p>}
          {sent && (
            <p className="m-0 flex items-center gap-1.5 text-[13px] font-medium text-primary">
              <Check className="size-4" />
              Thanks — we&apos;ll be in touch.
            </p>
          )}
        </form>
      </CardContent>
    </Card>
  );
}
